import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MlMetricLabel, MlSectionTitle } from '../../components/MlEstimateBadge';
import {
  getMarketShareChartData,
  getQualityColor,
  getSentimentColor,
} from './helpers';

export default function MlOverviewTab({
  clientSummary,
  competitiveOverview,
  onCompetitorClick,
}) {
  const marketShareChartData = getMarketShareChartData(competitiveOverview);
  const competitors = Array.isArray(
    competitiveOverview?.market_share_distribution
  )
    ? competitiveOverview.market_share_distribution
    : [];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Executive Summary
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Market Overview
              </Typography>
              <Stack spacing={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body2">Total Competitors:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {clientSummary.total_competitors}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body2">Total Followers:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {(clientSummary.total_followers || 0).toLocaleString()}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body2">Total Posts:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {(clientSummary.total_posts || 0).toLocaleString()}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body2">Platforms Tracked:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {clientSummary.platforms_tracked?.length || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Performance Metrics
              </Typography>
              <Stack spacing={1}>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}
                  >
                    <MlMetricLabel basis="quality_rubric">
                      <Typography variant="body2">Avg Quality Score:</Typography>
                    </MlMetricLabel>
                    <Typography variant="body2" fontWeight="medium">
                      {((clientSummary.avg_quality_score || 0) * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(clientSummary.avg_quality_score || 0) * 100}
                    color={getQualityColor(clientSummary.avg_quality_score || 0)}
                    sx={{ height: 6, borderRadius: 1 }}
                  />
                </Box>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}
                  >
                    <MlMetricLabel basis="sentiment_nlp">
                      <Typography variant="body2">Avg Sentiment:</Typography>
                    </MlMetricLabel>
                    <Typography variant="body2" fontWeight="medium">
                      {((clientSummary.avg_sentiment || 0.5) * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(clientSummary.avg_sentiment || 0.5) * 100}
                    color={getSentimentColor(clientSummary.avg_sentiment || 0.5)}
                    sx={{ height: 6, borderRadius: 1 }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {marketShareChartData.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <MlSectionTitle basis="market_share" variant="subtitle1">
              Engagement Share by Competitor
            </MlSectionTitle>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 2 }}
            >
              Share of voice from stored post engagement in the last snapshot
              window — not platform-reported market share.
            </Typography>
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={marketShareChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-35} textAnchor="end" height={90} />
                <YAxis unit="%" />
                <RechartsTooltip />
                <Legend />
                <Bar
                  dataKey="marketShare"
                  fill="#58a6ff"
                  name="Engagement share (%)"
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {competitors.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Competitors
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 2 }}
            >
              Click a row to open competitor detail (posts, analytics, and SWOT
              insights).
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>Competitor</TableCell>
                    <TableCell align="right">Engagement share</TableCell>
                    <TableCell align="right">Followers</TableCell>
                    <TableCell align="right">Posts</TableCell>
                    <TableCell align="right" width={48} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {competitors.map((row) => (
                    <TableRow
                      key={row.competitor_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() =>
                        onCompetitorClick?.({
                          competitor_id: row.competitor_id,
                          name: row.competitor_name,
                          market_share: row.market_share,
                        })
                      }
                    >
                      <TableCell>{row.rank ?? '—'}</TableCell>
                      <TableCell>{row.competitor_name}</TableCell>
                      <TableCell align="right">
                        {Number(row.market_share || 0).toFixed(1)}%
                      </TableCell>
                      <TableCell align="right">
                        {(row.followers || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">{row.posts_count ?? 0}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label={`Open ${row.competitor_name} details`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onCompetitorClick?.({
                              competitor_id: row.competitor_id,
                              name: row.competitor_name,
                              market_share: row.market_share,
                            });
                          }}
                        >
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
